import {
  AccessLevel,
  Event,
  EventType,
  TicketV2,
  Visibility,
} from "@markit/common.types";
import { useDispatch, useSelector } from "react-redux";
import { getAccountState } from "../../redux/slices/accountSlice";
import {
  friendlyRoleName,
  GetDateWithTime,
  isEventExternalLink,
  isExternalEventbrite,
  isExternalEventSpecificLink,
  isLinkedEventType,
  isPaidTickets,
  isProductSpecificType,
  truncateString,
} from "@markit/common.utils";
import { useCallback, useContext, useMemo, useState } from "react";
import { useTheme } from "../../hooks/useTheme";
import { MixpanelContext } from "../../context/AnalyticsService";
import { Icon } from "@iconify/react";
import StandardBorderedContainer from "../Containers/StandardBorderedContainer";
import { VerticalDivider } from "../Dividers/VerticalDivider";
import { DataLoaders } from "../../redux/slices/dataSlice";
import CustomLinkify from "../Links/CustomLinkify";
import { useNavigate } from "../../hooks/useNavigate";
import FatHeaderActionButton from "../Buttons/FatHeaderActionButton";
import CreateFormAndTrackingLinkModal from "../CreatorDashboard/FormSubmissions/CreateFormAndTrackingLinkModal";
import { getShareDomain } from "../../utils/shareLink";
import EventDateTimeDisplay from "../FullEvent/EventDateTimeDisplay";
import { FormatCurrency } from "../../utils/FormatCurrency";

type EventDashboardHeaderProps = {
  event: Event;
  userTicket: TicketV2 | undefined;
  openShareModal: (source: string) => void;
};

export const EventDashboardHeader = (props: EventDashboardHeaderProps) => {
  const { event, userTicket, openShareModal } = props;
  const { accountData } = useSelector(getAccountState).account;
  const { theme } = useTheme();
  const mixpanel = useContext(MixpanelContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [createFormVisible, setCreateFormVisible] = useState(false);

  const isProductSpecific = useMemo(
    () => isProductSpecificType(event.eventType),
    [event]
  );

  const isFormOrProduct = useMemo(
    () => event.eventType === EventType.GENERIC_LINK || isProductSpecific,
    [event.eventType, isProductSpecific]
  );

  const roleName = useMemo(
    () =>
      isEventExternalLink(event.eventType)
        ? event.externalLink
        : friendlyRoleName(userTicket),
    [event.eventType, event.externalLink, userTicket]
  );

  const eventEdit = useMemo(
    () =>
      accountData.isAdmin || (userTicket ? userTicket.role.eventEdit : false),
    [accountData.isAdmin, userTicket]
  );

  const styles = {
    containerView: {
      padding: "12px 14px",
    },

    mainText: {
      color: theme.PrimaryText.color,
      fontSize: 14,
      fontWeight: "600",
    },

    secondaryText: {
      color: theme.LabelText.color,
      fontSize: 14,
    },

    buttonContainer: {
      whiteSpace: "nowrap",
      borderRadius: 100,
      paddingBlock: 10,
      paddingInline: 12,
    },
  };

  const editOnPress = useCallback(() => {
    if (eventEdit && event) {
      mixpanel.track("Pressed Event Dashboard Edit", {
        distinct_id: accountData.uid,
        event_id: event.id,
        platform: "webapp",
        event_type: event.eventType,
      });
      if (event.eventType === EventType.GENERIC_LINK) {
        setCreateFormVisible(true);
      } else if (isProductSpecificType(event.eventType)) {
        dispatch(DataLoaders.event(event.id));
        const formattedEventType = event.eventType
          .toLowerCase()
          .replace(/\s/g, "-");
        navigate(`/e/${event.id}/edit-${formattedEventType}`, {
          state: { eventId: event.id },
        });
      } else if (isExternalEventSpecificLink(event.eventType)) {
        navigate(`/e/${event.id}/editLink`, {
          state: { confirmState: true, event: event },
        });
      } else {
        dispatch(DataLoaders.event(event.id));
        navigate(`/e/${event.id}/edit`, {
          state: { editEvent: event },
        });
      }
    }
  }, [
    accountData.uid,
    dispatch,
    event,
    eventEdit,
    mixpanel,
    navigate,
    setCreateFormVisible,
  ]);

  const previewOnPress = useCallback(() => {
    window.open(`${getShareDomain()}/e/${event.id}`, "_blank");
  }, [event.id]);

  return (
    <>
      <div className="RowNormalSpaced" style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 32, fontWeight: "600" }}>{event.title}</div>
        {!isLinkedEventType(event.eventType) ? (
          <div className="AlignedRow" style={{ marginLeft: 20, gap: 10 }}>
            <FatHeaderActionButton
              title="Preview"
              onPress={previewOnPress}
              icon={
                <Icon
                  icon={"ion:arrow-forward"}
                  height={17}
                  style={{ transform: "rotateY(0deg) rotate(-45deg)" }}
                />
              }
            />
            {eventEdit ? (
              <FatHeaderActionButton
                title="Edit"
                onPress={editOnPress}
                icon={
                  <Icon
                    icon={"ion:create"}
                    height={17}
                    style={{ marginTop: -2 }}
                  />
                }
              />
            ) : null}
            <FatHeaderActionButton
              title={event.isManualSoldOut ? "Inactive" : "Share"}
              icon={
                event.isManualSoldOut ? undefined : (
                  <Icon
                    icon={"ion:arrow-forward"}
                    height={17}
                    style={{ transform: "rotateY(0deg) rotate(-45deg)" }}
                  />
                )
              }
              disabled={event.isManualSoldOut}
              onPress={() => openShareModal("form header")}
            />
          </div>
        ) : null}
      </div>
      <StandardBorderedContainer>
        {isFormOrProduct ? (
          <div className="AlignedRow">
            <div
              className="AlignedRow"
              style={{ ...styles.containerView, gap: 7, maxWidth: 100 }}
            >
              <Icon
                icon={
                  event.visibility === Visibility.Private
                    ? "ion:lock-closed"
                    : "ion:lock-open"
                }
                height={15}
              />
              <span className="bodyMedium">
                {event.visibility === Visibility.Private ? "Private" : "Public"}
              </span>
            </div>
            <VerticalDivider height={41} />
            {isProductSpecific && event.accessLevel !== AccessLevel.ALL ? (
              <>
                <div className="AlignedRow" style={{ ...styles.containerView }}>
                  {event.accessLevel === AccessLevel.CHECKOUT ? (
                    isPaidTickets(event) ? (
                      <div className="AlignedRow" style={{ gap: 7 }}>
                        <span className="bodyMedium">Paid</span>
                        <span className="bodySubtext">
                          {FormatCurrency(event.customTickets[0].price / 100)}
                        </span>
                      </div>
                    ) : (
                      <span className="bodyMedium">Checkout</span>
                    )
                  ) : (
                    <span className="bodyMedium">{event.accessLevel} Only</span>
                  )}
                </div>
                <VerticalDivider height={41} />
              </>
            ) : null}
            <div style={{ ...styles.containerView }}>
              <span className="bodySubtext TextOverflowEllipsis">
                Created:{" "}
                {GetDateWithTime(event.createdAt, true, event.timezone)}
              </span>
            </div>
            {event.externalLink ? (
              <>
                <VerticalDivider height={41} />
                <div style={{ ...styles.containerView, maxWidth: 220 }}>
                  <CustomLinkify noUnderline altColor={theme.SubText.color}>
                    <span className="bodySubtext TextOverflowEllipsis">
                      {event.externalLink}
                    </span>
                  </CustomLinkify>
                </div>
              </>
            ) : null}
          </div>
        ) : (
          <div className="AlignedRow">
            <div style={{ ...styles.containerView, maxWidth: 240 }}>
              <CustomLinkify noUnderline altColor={theme.PrimaryText.color}>
                <span
                  className="TextOverflowEllipsis"
                  style={{ ...styles.mainText }}
                >
                  {roleName}
                </span>
              </CustomLinkify>
            </div>
            <VerticalDivider height={41} />
            <div
              className="AlignedRow"
              style={{ ...styles.containerView, gap: 7 }}
            >
              <Icon
                icon={"ion:calendar"}
                height={12}
                style={{
                  color: theme.LabelText.color,
                }}
              />
              <EventDateTimeDisplay
                event={event}
                className={"AlignedRow"}
                containerStyles={{ gap: 7 }}
              />
            </div>
            {!isExternalEventbrite(event.eventType) ? (
              <>
                <VerticalDivider height={41} />
                <div
                  className="AlignedRow"
                  style={styles.containerView}
                  onClick={() => {
                    if (!event.isVirtual) {
                      window.open(
                        "https://www.google.com/maps/search/?api=1&query=Google&query_place_id=" +
                          event.googlePlaceId
                      );
                    }
                  }}
                >
                  <Icon
                    icon={event.isVirtual ? "mdi:video" : "mdi:map-marker"}
                    height={15}
                    style={{
                      color: theme.LabelText.color,
                    }}
                  />
                  <div style={{ ...styles.mainText, marginLeft: 7 }}>
                    {event.isVirtual
                      ? "Virtual"
                      : truncateString(event.googleDescription, 35, "...")}
                  </div>
                  <div style={{ ...styles.secondaryText, marginLeft: 7 }}>
                    {event.isVirtual
                      ? ""
                      : truncateString(event.formattedAddress, 30, "...")}
                  </div>
                  {!event.isVirtual ? (
                    <Icon
                      icon={"ion:open-outline"}
                      height={14}
                      style={{
                        color: theme.LabelText.color,
                        marginLeft: 4,
                      }}
                    />
                  ) : null}
                </div>
              </>
            ) : null}
          </div>
        )}
      </StandardBorderedContainer>
      {createFormVisible ? (
        <CreateFormAndTrackingLinkModal
          isVisible={createFormVisible}
          setIsVisible={setCreateFormVisible}
          existingForm={event}
          eventType={event.eventType}
        />
      ) : null}
    </>
  );
};
